<!--
  ReplayBlock — pairs a <video> with a MiniQuadra whose dots (players + ball)
  are scrubbed from a persisted `courtTimeline`. Impacts accumulate as video
  time crosses each swing's `contactTimeMs`, so playing back the session
  shows the rally unfold on the mini-court in sync.
-->
<script lang="ts">
  import { onDestroy, onMount } from 'svelte';
  import MiniQuadra from './MiniQuadra.svelte';
  import type {
    CourtImpact,
    CourtTimelineSample,
    LiveBall,
    LivePlayers,
  } from './types';

  interface ReplayBlockProps {
    videoUrl: string;
    timeline: readonly CourtTimelineSample[];
    impacts: readonly CourtImpact[];
    /**
     * Per-impact `contactTimeMs` into the video. `impacts[i]` and
     * `contactTimesMs[i]` are paired by index. When omitted, all impacts
     * show from t=0 (legacy behaviour).
     */
    contactTimesMs?: readonly number[];
  }

  const { videoUrl, timeline, impacts, contactTimesMs = [] }: ReplayBlockProps = $props();

  let videoEl = $state<HTMLVideoElement | null>(null);
  let currentMs = $state(0);
  let rafId: number | null = null;

  // Track playing state via a `$state` so rAF drives updates only while
  // the video is playing — `timeupdate` alone fires at ~4 Hz, too coarse
  // for the dots to look like they move smoothly.
  let isPlaying = $state(false);

  function tick() {
    if (!videoEl) return;
    currentMs = videoEl.currentTime * 1000;
    if (!videoEl.paused && !videoEl.ended) {
      rafId = requestAnimationFrame(tick);
    } else {
      rafId = null;
    }
  }

  function onPlay() {
    isPlaying = true;
    if (rafId === null) rafId = requestAnimationFrame(tick);
  }

  function onPauseOrEnd() {
    isPlaying = false;
    if (videoEl) currentMs = videoEl.currentTime * 1000;
  }

  function onSeeked() {
    if (videoEl) currentMs = videoEl.currentTime * 1000;
  }

  onMount(() => {
    if (videoEl) currentMs = videoEl.currentTime * 1000;
  });

  onDestroy(() => {
    if (rafId !== null) cancelAnimationFrame(rafId);
  });

  // Binary search: find the timeline sample with the largest `tMs` ≤ t.
  // Returns null when the timeline is empty or the first sample is already
  // in the future.
  function sampleAt(t: number): CourtTimelineSample | null {
    const n = timeline.length;
    if (n === 0) return null;
    if (t < timeline[0]!.tMs) return null;
    let lo = 0;
    let hi = n - 1;
    while (lo < hi) {
      const mid = (lo + hi + 1) >>> 1;
      if (timeline[mid]!.tMs <= t) lo = mid;
      else hi = mid - 1;
    }
    return timeline[lo] ?? null;
  }

  const currentSample = $derived(sampleAt(currentMs));

  const livePlayers = $derived<LivePlayers>({
    p1: currentSample?.p1 ?? null,
    p2: currentSample?.p2 ?? null,
  });

  const liveBall = $derived<LiveBall | null>(
    currentSample?.ball
      ? {
          x: currentSample.ball.x,
          y: currentSample.ball.y,
          confidence: currentSample.ballConf,
        }
      : null,
  );

  // Only show impacts that have already been "struck" by the current
  // playhead. If `contactTimesMs` isn't provided, show them all.
  const visibleImpacts = $derived.by<readonly CourtImpact[]>(() => {
    if (contactTimesMs.length !== impacts.length) return impacts;
    const out: CourtImpact[] = [];
    for (let i = 0; i < impacts.length; i++) {
      if ((contactTimesMs[i] ?? 0) <= currentMs) out.push(impacts[i]!);
    }
    return out;
  });

  const lastEvent = $derived(
    visibleImpacts.length > 0
      ? {
          player: visibleImpacts[visibleImpacts.length - 1]!.player,
          zone: visibleImpacts[visibleImpacts.length - 1]!.zone,
          speed: visibleImpacts[visibleImpacts.length - 1]!.speed,
          shotType: visibleImpacts[visibleImpacts.length - 1]!.shotType,
        }
      : null,
  );
</script>

<div class="flex flex-col gap-3 md:grid md:grid-cols-2">
  <div class="w-full overflow-hidden rounded-md bg-black">
    <!-- svelte-ignore a11y_media_has_caption -->
    <video
      bind:this={videoEl}
      src={videoUrl}
      class="block w-full"
      controls
      playsinline
      onplay={onPlay}
      onpause={onPauseOrEnd}
      onended={onPauseOrEnd}
      onseeked={onSeeked}
      ontimeupdate={onSeeked}
    ></video>
  </div>

  <div class="min-h-[320px]">
    <MiniQuadra
      impacts={visibleImpacts}
      {livePlayers}
      {liveBall}
      {lastEvent}
      compact
    />
  </div>
</div>
